import { formatDistanceToNow } from "date-fns";
import { Check, X, ChevronDown, Trash2 } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Tooltip } from "@/shared/ui/tooltip";
import type { User } from "@/services/api/users";
import type { Category } from "@/services/api/categories";
import { getCategoryLabel } from "@/features/categories/utils/categoryUtils";

interface UserTableRowProps {
  user: User;
  categories: Category[];
  isLoadingCategories: boolean;
  isUpdatingPermissions: boolean;
  isSelf?: boolean;
  onTogglePermission: (
    user: User,
    category: Category,
    checked: boolean
  ) => void;
  onDelete?: (user: User) => void;
}

const dateOrDash = (iso?: string | null) =>
  iso ? `${formatDistanceToNow(new Date(iso))} ago` : "—";

export function UserTableRow({
  user,
  categories,
  isLoadingCategories,
  isUpdatingPermissions,
  isSelf,
  onTogglePermission,
  onDelete,
}: UserTableRowProps) {
  const isRegistered = !!user.entraObjectId;
  const selectedIds = user.categoryPermissions?.map((c) => c.categoryId) || [];

  const handleRemoveCategory = (categoryId: number) => () => {
    const category = categories.find((c) => Number(categoryId) === c.id);
    if (!category) return;
    onTogglePermission(user, category, false);
  };
  return (
    <tr key={user.entraObjectId} className="hover:bg-grid-50">
      <td className="px-6 py-4">
        <div className="flex flex-col gap-1">
          <span className="font-medium text-ink-900">{user.email}</span>
          <span className="text-sm text-ink-500">{user.displayName}</span>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center justify-center">
          {user.hasLoggedIn ? (
            <Check className="h-5 w-5 text-imperial-600" />
          ) : (
            <X className="h-5 w-5 text-ink-400" />
          )}
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-ink-600">
        {dateOrDash(user.createdDate)}
      </td>
      <td className="px-6 py-4 text-sm text-ink-600">
        {dateOrDash(user.lastLoginAt)}
      </td>
      <td className="px-6 py-4">
        {!isRegistered ? (
          <div className="relative min-w-[200px]">
            <div className="flex flex-wrap items-center gap-2">
              {user.categoryPermissions &&
              user.categoryPermissions.length > 0
                ? user.categoryPermissions.map((perm) => (
                    <Badge
                      key={`${user.id}-${perm.categoryId}`}
                      variant="secondary"
                      className="text-xs font-normal"
                    >
                      {getCategoryLabel({ name: perm.categoryName, displayName: perm.categoryDisplayName })}
                    </Badge>
                  ))
                : <span className="text-xs text-ink-400">—</span>}
            </div>
            <Tooltip
              content="Not registered"
              description="Categories can only be changed after the user has registered via MS Entra."
            >
              <div className="absolute inset-0 cursor-default" />
            </Tooltip>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 min-w-[200px]">
            {user.role?.toUpperCase() === "ADMIN" ? (
              <Badge variant="secondary" className="text-xs font-normal">
                Full Access
              </Badge>
            ) : (
              <>
                {user.categoryPermissions &&
                user.categoryPermissions.length > 0 ? (
                  <>
                    {user.categoryPermissions.map((perm) => (
                      <Badge
                        key={`${user.entraObjectId}-${perm.categoryId}`}
                        variant="secondary"
                        className="text-xs font-normal flex items-center gap-1.5"
                      >
                        {getCategoryLabel({ name: perm.categoryName, displayName: perm.categoryDisplayName })}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={handleRemoveCategory(perm.categoryId)}
                          className="ml-1 h-4 w-4 rounded-full hover:bg-grid-200 p-0 transition-colors"
                          aria-label={`Remove ${perm.categoryName}`}
                          disabled={isUpdatingPermissions}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </>
                ) : null}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1.5 text-xs border-dashed"
                      disabled={isLoadingCategories || isUpdatingPermissions}
                    >
                      Add categories
                      <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="max-h-80 overflow-y-auto">
                    {categories.map((category) => (
                      <DropdownMenuCheckboxItem
                        key={category.id}
                        checked={selectedIds.includes(Number(category.id))}
                        onCheckedChange={(checked) =>
                          onTogglePermission(user, category, Boolean(checked))
                        }
                      >
                        {getCategoryLabel(category)}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        )}
      </td>
      <td className="px-6 py-4">
        <Badge
          variant={user.entraObjectId ? "outline" : "secondary"}
          className="uppercase text-xs font-medium"
        >
          {user.entraObjectId ? user.role || "USER" : "PENDING"}
        </Badge>
      </td>
      <td className="px-6 py-4">
        {isSelf ? (
          <Tooltip content="You cannot delete your own account">
            <span className="inline-flex">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-ink-400"
                disabled
                aria-label="Cannot delete yourself"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-ink-400 hover:text-red-600"
            onClick={() => onDelete?.(user)}
            aria-label={`Delete ${user.email}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </td>
    </tr>
  );
}
